import { Component, Inject, inject, OnInit, signal, ViewChild } from '@angular/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { LabdataService } from '../../../services/labdata.service';
import { AbstractControl, FormArray, FormControl, FormGroup, FormsModule, NgModel, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { LabCaptureData, labDataInput, LabDataPayload, SaveLabDataCaptureRequest } from '../../../interfaces/labDataModel';
import { labDataCaptureSiteTanksDigestorsResponse } from '../../../interfaces/siteType';
import { MatCardModule } from "@angular/material/card";
import { AlertDialogComponent } from '../../shared/components/alert-dialog/alert-dialog.component';
import { MatDatepickerModule } from "@angular/material/datepicker";
import { DragDropModule } from '@angular/cdk/drag-drop';
import { numericInputLimit } from '../../shared/commonutils/helper';
import { LookUpService } from '../../../services/lookupservice';
import { IdName } from '../../../interfaces/pricematrixmodel';

@Component({
  selector: 'app-addedit-labdatacapture',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatTabsModule,
    MatSelectModule,
    MatCardModule,
    MatDatepickerModule,
    DragDropModule
],
  templateUrl: './addedit-labdatacapture.component.html',
  styleUrl: './addedit-labdatacapture.component.scss'
})

export class AddeditLabdatacaptureComponent implements OnInit {
  @ViewChild('dcctrl') dcctrl!:NgModel;
  private service = inject(LabdataService);  
  displayValue = signal<string>('');
  private dialog = inject(MatDialog);
  readonly MAX_VAL = 999999999.99;
  ldcForm= new FormGroup(
    {
      tanks: new FormArray<FormGroup>([]), 
    }, 
    { validators: this.service.atLeastOneFieldValidator }
  );
  siteId = signal<string>('');
  isEditMode = signal<boolean>(false);
  labTypes:IdName[] = [];
  filteredLabTypes:IdName[] = [];
  tabs: labDataCaptureSiteTanksDigestorsResponse[] = [];
  datePipe = inject(DatePipe);
  text: string = '';
  canShowSaveButton = signal<boolean>(false);
  formatAndClampNumber(value: any, decimals: number, max?: number): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const num = Number(value);
    if (isNaN(num)) {
      return null;
    }
    max = max ?? this.MAX_VAL;
    let clampedNum = num > max ? max : num;
    return parseFloat(clampedNum.toFixed(decimals));
  }
  get tanksFormArray(): FormArray {
    return this.ldcForm.get('tanks') as FormArray;
  }
  selectedTabIndex = signal(0);

  constructor(public dialogRef: MatDialogRef<AddeditLabdatacaptureComponent>,
    @Inject(MAT_DIALOG_DATA) public data: labDataInput, private lookup: LookUpService){    
    console.log('Received data in dialog:', data);
    if(data.isEdit){
      this.text = `(${data.id})`;
    }
    data.userName = data.userName.split('@')[0];
    this.siteId.set(data.siteId);
    this.isEditMode.set(data.isEdit);
    this.canShowSaveButton.set(data.canShowSaveButton);
    if(!data.isEdit){
      data.labDataId = null;
    }
  }

  ngOnInit(): void {
    this.getTabsInfo();
  }

  getTabsInfo() {    
    this.lookup.getLabTypes().subscribe({
      next: (data: IdName[]) => {
        this.labTypes = data;
        this.filteredLabTypes = [...this.labTypes];
      }
    });
    this.service.getLabDataTabs(this.siteId().toString()).subscribe((tabsInfo) => {
      this.tabs = tabsInfo;
      this.tanksFormArray.clear();
      this.tabs.forEach(() => {
        this.tanksFormArray.push(this.createTabGroup());
      });
      this.ldcForm.updateValueAndValidity();
      if (this.isEditMode()) {
        this.navigateToEditTab();
        this.fetchAndPatchData();
      }
    });
  }
  
  navigateToEditTab(): void {
    const tankId     = this.data.editTankId;
    const digesterId = this.data.editDigesterId;

    if (!tankId && !digesterId) return;

    const tabIndex = this.tabs.findIndex(tab =>
      (tab.isTank     && tab.id === tankId)     ||
      (tab.isDigester && tab.id === digesterId)
    );

    if (tabIndex !== -1) {
      this.selectedTabIndex.set(tabIndex);
    }
  }

  filterLabData(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    if (!searchTerm) {
      this.filteredLabTypes = [...this.labTypes];
    } else {
      this.filteredLabTypes = this.labTypes.filter(
        g => g.name.toLowerCase().includes(searchTerm)
      );
    }
  }

  fetchAndPatchData() {
    const siteId = this.siteId();
    const dateCaptured = this.data.dateCaptured;
    this.service.getLabDataCaptureData(siteId.toString(), dateCaptured, dateCaptured).subscribe((response) => {
      console.log('Fetched existing lab data for editing:', response);
      response.forEach((dataEntry) => {
        const tabIndex = this.tabs.findIndex(tab => 
          (tab.isTank && tab.id === dataEntry.tankId) ||
          (tab.isDigester && tab.id === dataEntry.digesterId)
        );
        if (tabIndex !== -1) {
          const formGroup = this.tanksFormArray.at(tabIndex) as FormGroup;
          formGroup.patchValue({
            id: dataEntry.id,
            siteId: dataEntry.siteId,
            tankId: dataEntry.tankId,
            digesterId: dataEntry.digesterId,
            cod: dataEntry.cod,
            total_solids: dataEntry.total_solids,
            volatile_solids: dataEntry.volatile_solids,
            ammonia: dataEntry.ammonia,
            sulfate: dataEntry.sulfate,
            //coD_outlet: dataEntry.coD_outlet,
            pH: dataEntry.pH,
            tic: dataEntry.tic,
            vfa: dataEntry.vfa,
            vfaByTIC: dataEntry.vfaByTIC,
            vfA_Acetic: dataEntry.vfA_Acetic,
            vfA_ropionic: dataEntry.vfA_ropionic,
            vfA_Butyric: dataEntry.vfA_Butyric,
            vfA_Valeric: dataEntry.vfA_Valeric,
            tkn: dataEntry.tkn,
            dissolved_sulfur: dataEntry.dissolved_sulfur,
            chlorides: dataEntry.chlorides,
            totalOrganicCarbon: dataEntry.totalOrganicCarbon,
            phosphorus: dataEntry.phosphorus,
            potassium: dataEntry.potassium,
            oilandGrease: dataEntry.oilandGrease,
            iron: dataEntry.iron,
            vFAIsovaleric: dataEntry.vfaIsovaleric,
            vFASuccinic: dataEntry.vfaSuccinic,
            vFACaproic: dataEntry.vfaCaproic,
            vFALactic: dataEntry.vfaLactic,
          });
        }
      });
    });
  }

  createTabGroup(): FormGroup {
    return new FormGroup({
      id: new FormControl<number>(0),
      cod: this.createCaptureControl(),
      total_solids: this.createCaptureControl(),
      volatile_solids: this.createCaptureControl(),
      ammonia: this.createCaptureControl(),
      sulfate: this.createCaptureControl(),
      pH: this.createCaptureControl(),
      tic: this.createCaptureControl(),
      vfa: this.createCaptureControl(),
      vfaByTIC: this.createCaptureControl(),
      vfA_Acetic: this.createCaptureControl(),
      vfA_ropionic: this.createCaptureControl(),
      vfA_Butyric: this.createCaptureControl(),
      vfA_Valeric: this.createCaptureControl(),
      tkn: this.createCaptureControl(),
      dissolved_sulfur: this.createCaptureControl(),
      chlorides: this.createCaptureControl(),
      totalOrganicCarbon: this.createCaptureControl(),
      phosphorus: this.createCaptureControl(),
      potassium: this.createCaptureControl(),
      oilandGrease: this.createCaptureControl(),
      iron: this.createCaptureControl(),
      vFAIsovaleric: this.createCaptureControl(),
      vFASuccinic: this.createCaptureControl(),
      vFACaproic: this.createCaptureControl(),
      vFALactic: this.createCaptureControl(),
    });
  }

  createCaptureControl(): FormControl<number | null> {
    return new FormControl<number | null>(null);
  }

  onBlur(event: Event, fieldName: keyof LabCaptureData, decimals: number, index: number, max?: number): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;
    const control = this.tanksFormArray.at(index).get(fieldName);
    const formattedValue = this.formatAndClampNumber(value, decimals, max);
    if (control) {
      control.setValue(formattedValue, { emitEvent: true });
    }
  }

  roundOffDynamic(event: Event,index: number, fieldName: string, maxValue: number, maxDecimals: number): void {
    const input = event.target as HTMLInputElement;
  
    let val = numericInputLimit(event, maxValue, maxDecimals);  
    this.displayValue.set(val);
    input.value = val;
  
    const ctrl = this.tanksFormArray.at(index).get(fieldName);
  
    const isValueEmpty = val === '' || val === '.';
  
    if (isValueEmpty) {
      ctrl?.setValue(null, { emitEvent: false });
    } else if (val.endsWith('.')) {
      ctrl?.setErrors({ incompleteDecimal: true });
    } else {
      ctrl?.setValue(parseFloat(val), { emitEvent: false });
    }
    requestAnimationFrame(() => {
      if (document.activeElement === input) {
          input.value = val;
      }
    });
  }

  onSave() {
    this.dcctrl.control?.markAsTouched();
    if(this.dcctrl.invalid) {
      return;
    }
    if (this.ldcForm.invalid) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please enter at least one data point before saving.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    
    const siteId = this.data.siteId;
    const labDataCapture: any[] = [];
    this.tanksFormArray.controls.forEach((control, index) => {
      const formGroup = control as FormGroup;
      const data = formGroup.getRawValue();
      const tab = this.tabs[index];
      const isDirty = formGroup.dirty;
      
      const numericFields = [
        'cod', 'total_solids', 'volatile_solids', 'ammonia', 'sulfate', 
        'coD_outlet', 'pH', 'tic', 'vfa', 'vfaByTIC', 'vfA_Acetic', 
        'vfA_ropionic', 'vfA_Butyric', 'vfA_Valeric', 'tkn', 'dissolved_sulfur',
        'chlorides','totalOrganicCarbon','phosphorus', 'potassium', 'oilandGrease',
        'iron', 'vFAIsovaleric', 'vFASuccinic', 'vFACaproic','vFALactic'
      ];

      // Logic: Only include if at least one field is non-zero/non-null
      const hasActualData = numericFields.some(field => {
        const val = data[field];
        return val !== null && val !== undefined && val !== '' && Number(val) !== 0;
      });
      
      const shouldInclude = hasActualData;

      if (shouldInclude) {
        const entry: any = {
          ID: data.id,
          SiteId: siteId,
          TankId: tab.isTank ? tab.id : 0,
          DigesterId: tab.isDigester ? tab.id : 0,
          COD: this.formatAndClampNumber(data.cod, 2) ?? 0,
          Total_solids: this.formatAndClampNumber(data.total_solids, 2) ?? 0,
          Volatile_solids: this.formatAndClampNumber(data.volatile_solids, 2) ?? 0,
          Ammonia: this.formatAndClampNumber(data.ammonia, 2) ?? 0,
          Sulfate: this.formatAndClampNumber(data.sulfate, 2) ?? 0,
          pH: this.formatAndClampNumber(data.pH, 2) ?? 0,
          TIC: this.formatAndClampNumber(data.tic, 2) ?? 0,
          VFA: this.formatAndClampNumber(data.vfa, 2) ?? 0,
          VFAByTIC: this.formatAndClampNumber(data.vfaByTIC, 2) ?? 0,
          VFA_Acetic: this.formatAndClampNumber(data.vfA_Acetic, 2) ?? 0,
          VFA_ropionic: this.formatAndClampNumber(data.vfA_ropionic, 2) ?? 0,
          VFA_Butyric: this.formatAndClampNumber(data.vfA_Butyric, 2) ?? 0,
          VFA_Valeric: this.formatAndClampNumber(data.vfA_Valeric, 2) ?? 0,
          TKN: this.formatAndClampNumber(data.tkn, 2) ?? 0,
          Dissolved_sulfur: this.formatAndClampNumber(data.dissolved_sulfur, 2) ?? 0,

          Chlorides: this.formatAndClampNumber(data.chlorides, 2) ?? 0,
          TotalOrganicCarbon: this.formatAndClampNumber(data.totalOrganicCarbon, 2) ?? 0,
          Phosphorus: this.formatAndClampNumber(data.phosphorus, 2) ?? 0,
          Potassium: this.formatAndClampNumber(data.potassium, 2) ?? 0,
          OilandGrease: this.formatAndClampNumber(data.oilandGrease, 2) ?? 0,
          Iron: this.formatAndClampNumber(data.iron, 2) ?? 0,
          VFAIsovaleric: this.formatAndClampNumber(data.vFAIsovaleric, 2) ?? 0,
          VFASuccinic: this.formatAndClampNumber(data.vFASuccinic, 2) ?? 0,
          VFACaproic: this.formatAndClampNumber(data.vFACaproic, 2) ?? 0,
          VFALactic: this.formatAndClampNumber(data.vFALactic, 2) ?? 0,
          LabId: this.data.labDataId === null ? null : this.data.labDataId ,
          LabReportId: this.data.labReportId,
          ReportDate: this.data.reportDate
        };
        labDataCapture.push(entry);
      }
    });

    if (labDataCapture.length === 0) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'No changes detected. Please modify at least one field before saving.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    const formattedDate: any = this.data.dateCaptured 
      ? this.datePipe.transform(this.data.dateCaptured, 'yyyy-MM-dd') 
      : this.datePipe.transform(new Date(), 'yyyy-MM-dd');

    var dataToSave: SaveLabDataCaptureRequest = {
      dateCaptured: formattedDate,
      labDataCapture: labDataCapture
    };
    console.log('Final Payload to be sent:', dataToSave);
    // 3. Call the mock save API
    this.service.saveLoadData(dataToSave).subscribe((response) => {
      console.log('Save response:', response);
      this.dialogRef.close(true);
    });
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  onKeydown(event: KeyboardEvent) {
    if(event.ctrlKey && event.metaKey){return;} 
    if(!/[0-9\/]/.test(event.key) && event.key.length === 1){
      event.preventDefault();
    }
  }
  
  dateformat(event: Event){
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if(!value) return;
    const parts = value.split('/');
    if(parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if(month.length === 1)  month = '0' + month;
    if(day.length === 1)  day = '0' + day;
    if(year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }
}
